'use client'

import { useState, useEffect, useRef, useMemo } from 'react'
import { useRouter } from 'next/navigation'
import { IconSearch, IconX, IconPackage } from '@tabler/icons-react'
import axios from 'axios'
import { useDebounce } from '@/hooks/useDebounce'
import FingerprintJS from '@fingerprintjs/fingerprintjs'

// Move outside component to avoid recreation
// const POPULAR_PACKAGES = ['react', 'vue', 'angular', 'next']
const PREFETCH_KEY = 'npm_prefetch_timestamp'

interface NpmPackage {
  name: string
  description: string
  version: string
}

const timeRanges = [
  { label: '1 Year', value: '1y' },
  { label: '6 Months', value: '6m' },
  { label: '3 Years', value: '3y' },
  { label: '5 Years', value: '5y' },
  { label: 'All Time', value: 'all' },
]

export default function PackageSearch() {
  const router = useRouter()
  const [selectedPackages, setSelectedPackages] = useState<string[]>([])
  const [inputValue, setInputValue] = useState('')
  const [timeRange, setTimeRange] = useState('1y')
  const [suggestions, setSuggestions] = useState<NpmPackage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const abortController = useRef<AbortController>(new AbortController())
  
  const debouncedSearchTerm = useDebounce(inputValue, 300)

  // Memoize filtered suggestions
  const filteredSuggestions = useMemo(() => {
    if (!suggestions.length) return []
    return suggestions.filter(pkg => 
      !selectedPackages.includes(pkg.name)
    )
  }, [suggestions, selectedPackages])

  // Prefetch popular packages only once per hour
  useEffect(() => {
    const prefetchPopular = async () => {
      const lastPrefetch = localStorage.getItem(PREFETCH_KEY)
      const now = Date.now()
      
      if (!lastPrefetch || now - parseInt(lastPrefetch) > 60 * 60 * 1000) { // 1 hour
        // await Promise.all(
        //   POPULAR_PACKAGES.map(q => 
        //     axios.get(`/api/packages/search?q=${q}`).catch(() => {})
        //   )
        // )
        localStorage.setItem(PREFETCH_KEY, now.toString())
      }
    }

    prefetchPopular()
  }, []) // Empty dependency array - only run once on mount

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch suggestions when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchSuggestions(debouncedSearchTerm)
    } else {
      setSuggestions([])
    }
  }, [debouncedSearchTerm])

  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([])
      return
    }

    // Cancel previous request if any
    if (abortController.current) {
      abortController.current.abort()
    }
    abortController.current = new AbortController()

    setIsLoading(true)
    try {
      const response = await axios.get(
        `/api/packages/search?q=${encodeURIComponent(query)}`,
        { signal: abortController.current.signal }
      )
      setSuggestions(response.data.packages)
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error('Error fetching suggestions:', error)
        setSuggestions([])
      }
    }
    setIsLoading(false)
  }

  const handleSelectPackage = (packageName: string) => {
    if (!selectedPackages.includes(packageName) && selectedPackages.length < 8) {
      setSelectedPackages([...selectedPackages, packageName])
      setInputValue('')
      setSuggestions([])
      setShowSuggestions(false)
    }
  }

  const handleSearch = async () => {
    // if (selectedPackages.length < 2) return
    
    try {
      // 获取浏览器指纹
      const fp = await FingerprintJS.load()
      const result = await fp.get()
      const browserId = result.visitorId

      // 记录搜索
      await fetch('/api/record-search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          packages: selectedPackages,
          browserId
        }),
      })
      // 导航到比较页面
      router.push(`/compare/${selectedPackages.sort().join('-vs-')}?timeRange=${timeRange}`)
    } catch (error) {
      console.error('Error recording search:', error)
      // 即使记录失败也继续导航
      router.push(`/compare/${selectedPackages.sort().join('-vs-')}?timeRange=${timeRange}`)
    }
  }

  return (
    <div className="flex flex-col gap-4 w-full max-w-3xl mx-auto">
      {/* Selected packages count indicator */}
      {selectedPackages.length > 0 && (
        <div className="text-sm text-gray-500 text-center">
          {selectedPackages.length} of 8 packages selected
        </div>
      )}

      <div className="flex gap-4">
        <div className="flex-1 relative" ref={searchRef}>
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value)
              setShowSuggestions(true)
            }}
            onFocus={() => setShowSuggestions(true)}
            placeholder={selectedPackages.length >= 8 ? 
              "Maximum packages selected" : 
              "Search NPM packages..."}
            disabled={selectedPackages.length >= 8}
            className="w-full px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:border-[#0066CC] disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          
          {/* Suggestions dropdown */}
          {showSuggestions && (inputValue || isLoading) && (
            <div className="absolute z-10 w-full mt-1 bg-white rounded-lg border border-[#E5E5E5] shadow-lg max-h-80 overflow-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">Loading...</div>
              ) : suggestions.length > 0 ? (
                suggestions.map((pkg) => (
                  <button
                    key={pkg.name}
                    onClick={() => handleSelectPackage(pkg.name)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-start gap-3"
                  >
                    <IconPackage className="mt-1 flex-shrink-0 text-gray-400" size={18} />
                    <div>
                      <div className="font-medium">{pkg.name}</div>
                      <div className="text-sm text-gray-500 line-clamp-2">
                        {pkg.description}
                      </div>
                    </div>
                  </button>
                ))
              ) : inputValue.length >= 2 ? (
                <div className="p-4 text-center text-gray-500">No packages found</div>
              ) : null}
            </div>
          )}

          {/* Clear input button */}
          {inputValue && (
            <button
              onClick={() => setInputValue('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <IconX size={20} className="text-gray-400" />
            </button>
          )}
        </div>
        
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-3 rounded-lg border border-[#E5E5E5] focus:outline-none focus:border-[#0066CC]"
        >
          {timeRanges.map((range) => (
            <option key={range.value} value={range.value}>
              {range.label}
            </option>
          ))}
        </select>

        <button
          onClick={handleSearch}
          disabled={selectedPackages.length === 0}
          className={`px-6 py-3 rounded-lg transition-colors flex items-center justify-center ${
            selectedPackages.length === 0
              ? 'bg-gray-300 cursor-not-allowed text-gray-500'
              : 'bg-[#0066CC] hover:bg-[#0052a3] text-white'
          }`}
          title={selectedPackages.length === 0 ? "Select packages to compare" : "Compare packages"}
        >
          <IconSearch size={20} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {selectedPackages.map((pkg) => (
          <span
            key={pkg}
            className="px-3 py-1 bg-[#0066CC] text-white rounded-full text-sm flex items-center gap-2"
          >
            {pkg}
            <button
              onClick={() => setSelectedPackages(selectedPackages.filter(p => p !== pkg))}
            >
              <IconX size={16} />
            </button>
          </span>
        ))}
      </div>
    </div>
  )
} 